import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../common/Header/Header';
import {
  ButtonLabel,
  Buttons,
  ButtonsSeparator,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import { Variant } from '../../components/Button/Button.types';
import Input from '../../components/Input/Input';
import Modal from '../../components/Modal/Modal';
import {
  fetchOrganizations,
  useMenu,
  useOrganizations,
  useOrganizationsDetails,
} from '../../services/menu/menu.service';
import { StyledButtons, StyledMessage } from '../Recipes/Recipes.styles';
import useTreeViewRowStates from '../../components/MenuTreeView/useMenuTreeViewRowStates';

import MenuTreeView from '../../components/MenuTreeView/MenuTreeView';
import {BuildRequestBodyParams, IndexableDataStructureWrapper } from '../../components/MenuTreeView/MenuTreeView.types';
import { MenusProps } from './Menus.types';
import Link from '../../components/Link/Link';
import { HeaderWrapper, StyledTitle } from './Menus.styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { Tooltip, TooltipProps, styled, tooltipClasses } from '@mui/material';
import theme from '../../theme';
import { Organization } from '../../services/menu/menu.types';
import MenuDumpModal from './components/MenuDumpModal';

export const Menus: FC<MenusProps> = () => {
  const navigate = useNavigate();
  const muiTheme = createTheme()
  const [modalOpen, setModalOpen] = useState(false);
  const [menuNo, setMenuNo] = useState('');
  const [menuNoError, setMenuNoError] = useState('');

  const [fetchEnabled, setFetchEnabled] = useState(false);
  const { data: menuData, error, remove } = useMenu(menuNo, fetchEnabled);
  const { data: orgs, isLoading} = useOrganizations({activateOrExtract: undefined}, true, 60000);
  const availableOrgs = orgs?.data || [];

  const [filterId, setFilterId] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  // const [searchError, setSearchError] = useState('');
  const [storeNoModal, setStoreNoModal] = useState(false);
  const [isStoreLoading, setIsStoreLoading] = useState(false);
  const [menuDumpModalOpen, setMenuDumpModalOpen] = useState(false);

  const openStoreNoModal = () => {
    setStoreNoModal(true);
    setSearchValue('');
    setFilterId('');
  }

  const closeModal = () => {
    setFetchEnabled(false);
    setMenuNo('');
    setModalOpen(false);
  };

  const closeStoreNoModal = () => {
    setSearchValue('');
    setStoreNoModal(false);
    setIsStoreLoading(false);
  };

  const handleSearch = async () => {
    setIsStoreLoading(true);
    await fetchOrganizations(
      { activateOrExtract: 'PENDING' },
      {storeNo: searchValue},
      'storeNo',
      (response: { data: Organization[] }) => {
        if (response.data.length > 0) {
          const orgRow = response.data[0];
          findSearchValue(searchValue, 'store', orgRow, orgRow);
          handleRowClick(orgRow, true);
          setFilterId(searchValue);
        }
      }
    );
    closeStoreNoModal();
  }

  const findSearchValue = (searchValue: string, type: string, row: Organization, orgRow: Organization) => {
    if (row.type === type && searchValue === row.id) {
      filterSiblings(row, orgRow, type);
    } else if (row.children) {
      row.children.forEach(childRow => findSearchValue(searchValue, type, childRow, orgRow))
    }
  }

  const filterSiblings = (targetRow: Organization, orgRow: Organization, type: string) => {
    handleRowClick(targetRow, true);
    orgRow.children?.forEach((row) => {
      if (targetRow?.parents && targetRow.parents[row.type] === row.id) {
        handleRowClick(row, true);
        filterSiblings(targetRow, row, type);
      }
    })
  }

  const openModal = () => setModalOpen(true);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (menuNoError) setMenuNoError('');
    setMenuNo(event.target.value);
  };

  const navigateToRecipe = () => {
    setFetchEnabled(true);
  };

  useEffect(() => {
    if (error) {
      setMenuNoError('Menu not found');
      setFetchEnabled(false);
      setMenuNo('');
      remove();
    }
  }, [error]);

  useEffect(() => {
    if (menuData?.data && menuNo !== '' && fetchEnabled) {
      navigate(`/menus/${menuNo}`);
    }
    return () => remove();
  }, [menuData, menuNo, fetchEnabled]);

  const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
      ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[5],
        fontSize: 14,
        height: '30px',
        paddingTop: '20px',
        },
        [`& .${tooltipClasses.arrow}`]: {
          color: theme.palette.common.white,
        },
  }));

  const AlertTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
      ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(255, 0, 0, 0.8)',
        boxShadow: theme.shadows[5],
        fontSize: 14,
        height: '30px',
        paddingTop: '20px',
        },
        [`& .${tooltipClasses.arrow}`]: {
          color: theme.palette.common.white,
        },
  }));

  function getAlertHeaderWrapper() {
    return <HeaderWrapper>
      <ThemeProvider theme={muiTheme}>
        <AlertTooltip title={'Incomplete'} arrow>
          <ErrorOutlineIcon fontSize='small' aria-label='Incomplete' style={{
            marginRight: '.5rem',
            color: 'red',
            marginLeft: '.5rem',
            alignContent: 'center',
          }} />
        </AlertTooltip>
      </ThemeProvider>
    </HeaderWrapper>;
  }

  function getEffectiveDateComponent(effectiveDate: string) {
    return <ThemeProvider theme={muiTheme}>
      <LightTooltip title={effectiveDate} arrow>
        <CalendarTodayOutlinedIcon fontSize='small' aria-label={effectiveDate} style={{
          marginRight: '.5rem',
          color: `${theme.colors.gray600}`,
          marginLeft: '.5rem',
        }} />
      </LightTooltip>
    </ThemeProvider>;
  }

  const dataStructure: IndexableDataStructureWrapper = {
    "org": {
      fetchChildren: useOrganizationsDetails,
      idKey: 'org',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
            </HeaderWrapper>
          </>
        );
      },
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { orgNo: params.row.id, activateOrExtract: undefined};
      },
      buildParentArray: () => [],
      getIndexByType: undefined
    },
    "territory": {
      idKey: 'territory',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
            </HeaderWrapper>
          </>
        );
      },
      buildParentArray: () => [],
      getIndexByType: undefined
    },
    "region": {
      idKey: 'region',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
            </HeaderWrapper>
          </>
        );
      },
      buildParentArray: () => [],
      getIndexByType: undefined
    },
    "market": {
      idKey: 'market',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
            </HeaderWrapper>
          </>
        );
      },
      buildParentArray: () => [],
      getIndexByType: undefined
    },
    "area": {
      idKey: 'area',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
            </HeaderWrapper>
          </>
        );
      },
      buildParentArray: () => [],
      getIndexByType: undefined
    },
    "store": {
      idKey: 'store',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
            </HeaderWrapper>
          </>
        );
      },
      buildParentArray: () => [],
      getIndexByType: undefined
    },
    "menu": {
      to: (params: BuildRequestBodyParams) => `/menus/${params.row.id}`,
      idKey: 'menuNo',
      showCheckboxes: false,
      buildLabel: (params: BuildRequestBodyParams) => {
        const newDate =  new Date(params.row.additionalProperties.effectiveDate);
        // We need to specify timezone because otherwise it will assume local browser timezone
        // So CST (UTC -6) will result in an offset by one day
        const effectiveDate = 'Effective: ' + newDate.toLocaleDateString("en-US", {timeZone: 'UTC'});

        return(
          <>
            <HeaderWrapper>
              {params.row.label}
              {params.row.hasExclamationMark && getAlertHeaderWrapper()}
              {params.row.additionalProperties.effectiveDate && getEffectiveDateComponent(effectiveDate)}
            </HeaderWrapper>
        </>
        );
      },
      buildParentArray: undefined, // the last element can't be expanded
      getIndexByType: undefined
    },
  }

  const { expandedRows, handleRowClick, resetLocalStorage } = useTreeViewRowStates(
    dataStructure,
    'selectMenus' + undefined,
  );

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Menus' />
      <Page>
        <TableWrapper>
          <HeaderWrapper>
          <StyledTitle>LIST OPTIONS</StyledTitle>
          <Link label={'Collapse'} onClick={resetLocalStorage} to={''} />
          </HeaderWrapper>
            <MenuTreeView
              height={'100%'}
              rows={availableOrgs}
              dataStructure={dataStructure}
              expandedRows={expandedRows}
              radioFilter={'store'}
              filterId={filterId}
              onRowClick={handleRowClick}
              isLoading={isLoading}
            />
        </TableWrapper>
        <Buttons>
          <ButtonLabel>Menus Actions</ButtonLabel>
          <Button testId='openMenuActionButton' label='Open' variant={Variant.Secondary} onClick={openModal} />
          <Button testId='compareMenuActionButton' label='Compare' variant={Variant.Secondary} onClick={() => navigate('/menus/compare')} />
          <Button testId='compareInvMenuActionButton' label='Compare Inv.' variant={Variant.Secondary} onClick={() => navigate('/menus/compareinv')} />
          <Button testId='menuDumpActionButton' label='Menu Dump' variant={Variant.Secondary} onClick={() => setMenuDumpModalOpen(true)} />
          <ButtonsSeparator />
          <ButtonLabel>Spec Actions</ButtonLabel>
          <Button testId='changeSpecMenuActionButton' label='Change' variant={Variant.Secondary} onClick={() => navigate(`/menus/changespecs`)} />
          <ButtonsSeparator />
          <ButtonLabel>Menu Date & Description</ButtonLabel>
          <Button testId='bulkEditMenuActionButton' label='Bulk Edit' variant={Variant.Secondary} onClick={() => navigate(`/menus/bulkedit`)} />
          <ButtonsSeparator />
          <ButtonLabel>Store Actions</ButtonLabel>
          <Button testId='findStoreNoActionButton' label='Find Store' variant={Variant.Secondary} onClick={openStoreNoModal} />
        </Buttons>
      </Page>
      <Modal
        id='openMenuByMenuNoModal'
        open={modalOpen}
        onClose={closeModal}
        title='Open Menu'
      >
        <div>
          <StyledMessage>Enter the Menu ID/Number you want to open.</StyledMessage>
          <Input
            label='Menu ID/Number'
            onChange={handleInputChange}
            value={menuNo}
            id='openMenuByMenuNo'
            errorText={menuNoError}
          />
          <StyledButtons>
            <Button testId='cancelMenuModalButton' variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
            <Button testId='openMenuModalButton' label='Open' onClick={navigateToRecipe} disabled={menuNo === ''} />
          </StyledButtons>
        </div>
      </Modal>
      <Modal
        id='findByStoreNoModal'
        open={storeNoModal}
        onClose={closeStoreNoModal}
        title='Find Store'
      >
        <div>
          <StyledMessage>Enter the Store number you want to find.</StyledMessage>
          <Input
            label='Store Number'
            onChange={(event: ChangeEvent<HTMLInputElement>) => {setSearchValue(event.target.value)}}
            value={searchValue}
            id='findByStoreNo'
            testId={'findByStoreNo'}
          />
          <StyledButtons>
            <Button testId='cancelStoreModalButton' variant={Variant.Secondary} label='Cancel' onClick={closeStoreNoModal} />
            <Button testId='confirmFindStoreModalButton' label='Find' onClick={handleSearch} disabled={searchValue === ''} isLoading={isStoreLoading} loadingLabel={'Searching...'} />
          </StyledButtons>
        </div>
      </Modal>
      <MenuDumpModal open={menuDumpModalOpen} onClose={() => setMenuDumpModalOpen(false)} />
    </>
  );
};

export default Menus;
